import { createColumnHelper } from "@tanstack/react-table";
import { FC, useCallback, useState } from "react";
import { Container, Form } from "react-bootstrap";
import { Link } from "react-router";

import { DataTable } from "@/components/DataTable";
import { FormatDate } from "@/components/Date";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageLoader } from "@/components/PageLoader";
import { TableWrapper } from "@/components/TableWrapper";
import { useTrackedQuery } from "@/hooks/useTrackedQuery";
import { FilterDef, useFacetFilter } from "@/hooks/useFacetFilter";
import { SosCustomFields } from "@/utils/sos/custom-fields";
import { OrderJob, orderJobsQueryOptions } from "./queries/orders-jobs";

interface TableRow extends OrderJob {
  customerBillingAddress: string;
  notes: string;
  pressureSystem: boolean;
  electricalDesigner: string;
  mechanicalDesigner: string;
  model: string;
  saleType: string;
  brand: string;
  pressureCertification: string;
  dateOpened: Date | null;
  dateClosed: Date | null;
  orderName: string;
  evolvedOpportunityNumber: number | null;
  serviceEstimateOpportunityNumber: number | null;
}

type OrderType = (typeof orderTypes)[number];

const orderTypes = ["All", "Sales Order", "Design Order", "Research Order"] as const;

const filterDefs = {
  closed: {
    options: [true, false],
    predicate: (job, includeClosed) => includeClosed || !job.closed,
    facetPredicate: (job, _includeClosed) => job.closed,
  } as FilterDef<boolean, TableRow>,
  archived: {
    options: [true, false],
    predicate: (job, includeArchived) => includeArchived || !job.archived,
    facetPredicate: (job, _includeArchived) => job.archived,
  } as FilterDef<boolean, TableRow>,
  type: {
    options: orderTypes,
    predicate: (job, filterValue) => {
      if (filterValue === "All") return true;
      if (filterValue === "Sales Order") return job.name.startsWith("SO-");
      if (filterValue === "Design Order") return job.name.startsWith("DO-");
      return job.name.startsWith("RO-");
    },
  } as FilterDef<OrderType, TableRow>,
};

const columnHelper = createColumnHelper<TableRow>();

const columns = [
  columnHelper.accessor("number", {
    header: "Order #",
    cell: ({ getValue }) => (
      <Link to={`/job/${getValue()}/details`} className="stretched-link">
        {getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor("brand", { header: "Brand" }),
  columnHelper.accessor("evolvedOpportunityNumber", { header: "Evolved #" }),
  columnHelper.accessor("serviceEstimateOpportunityNumber", { header: "Evolved # (Service Estimate)" }),
  columnHelper.accessor((x) => x.customer?.name, { id: "customer.name", header: "Customer" }),
  columnHelper.accessor("customerBillingAddress", { header: "Customer Location" }),
  columnHelper.accessor("saleType", { header: "Sale Type" }),
  columnHelper.accessor("model", { header: "Model" }),
  columnHelper.accessor("pressureSystem", {
    header: "Pressure System",
    cell: ({ getValue }) => (getValue() ? "✅" : "⬜"),
    meta: { cellProps: { className: "text-center" } },
  }),
  columnHelper.accessor("pressureCertification", { header: "Pressure Certification" }),
  columnHelper.accessor("electricalDesigner", { header: "Electrical Designer" }),
  columnHelper.accessor("mechanicalDesigner", { header: "Mechanical Designer" }),
  columnHelper.accessor("orderName", { header: "Order Name" }),
  columnHelper.accessor("notes", { header: "Notes" }),
  columnHelper.accessor("dateOpened", {
    header: "Date Opened",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  columnHelper.accessor("dateClosed", {
    header: "Date Closed",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  columnHelper.accessor("closed", {
    header: "Status",
    cell: ({ getValue }) => (getValue() ? "Inactive" : "Active"),
  }),
];

export const OrderLog: FC = () => {
  // Define State
  const [showClosed, setShowClosed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showTypes, setShowTypes] = useState<OrderType>("All");

  // Define callbacks
  const toggleShowClosed = useCallback(() => {
    setShowClosed(!showClosed);
  }, [showClosed]);
  const toggleShowArchived = useCallback(() => {
    setShowArchived(!showArchived);
  }, [showArchived]);
  const onShowTypeChanged = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setShowTypes(e.target.value as OrderType);
  }, []);

  // Fetch data
  const jobQuery = useTrackedQuery({
    ...orderJobsQueryOptions,
    select: (data) => {
      return data.map<TableRow>((job) => {
        const orderName = SosCustomFields.getStringValue(job, "orderName") || "";
        return {
          ...job,
          orderName,
          notes: (job.description ?? "").replace(orderName, "").trim(),
          customerBillingAddress: formatCustomerLocation(job.customer),
          brand: SosCustomFields.getListValue(job, "brand") || "",
          evolvedOpportunityNumber: SosCustomFields.getNumberValue(job, "evolvedOpportunityNumber"),
          serviceEstimateOpportunityNumber: SosCustomFields.getNumberValue(job, "serviceEstimateOpportunityNumber"),
          saleType: SosCustomFields.getListValue(job, "saleType") || "",
          model: SosCustomFields.getStringValue(job, "model") || "",
          pressureSystem: SosCustomFields.getBoolValue(job, "pressureSystem") || false,
          pressureCertification: SosCustomFields.getListValue(job, "pressureCertification") || "None",
          electricalDesigner: SosCustomFields.getStringValue(job, "electricalDesigner") || "",
          mechanicalDesigner: SosCustomFields.getStringValue(job, "mechanicalDesigner") || "",
          dateOpened: SosCustomFields.getDateValue(job, "dateOpened")!,
          dateClosed: SosCustomFields.getDateValue(job, "dateClosed"),
        };
      });
    },
  });

  // Apply filter and get all filter facet quantities
  const { filteredData, facetCounts } = useFacetFilter({
    filterDefs,
    data: jobQuery.data ?? [],
    selectedFilterValues: {
      closed: showClosed,
      archived: showArchived,
      type: showTypes,
    },
  });

  // Handle loading and error states
  if (jobQuery.isError) return `Error loading orders: ${jobQuery.error.message}`;
  if (jobQuery.isPending) return <PageLoader text="Loading Orders" />;

  return (
    <ErrorBoundary>
      <Container fluid className="py-3 h-100 d-flex flex-column">
        <div className="col-auto">
          <h2>
            Orders{" "}
            <small className="text-body-secondary">
              (Displaying {filteredData.length} of {jobQuery.data.length})
            </small>
          </h2>
          <p className="lead">Click on a row to load the detailed tracker for that order.</p>
          <div className="overflow-hidden mb-3">
            <div className="row row-cols-auto gx-4 align-items-center">
              <span>Filters:</span>
              <Form.Check
                type="switch"
                label={`Show Closed (${facetCounts.closed.get(true)!.toFixed(0)})`}
                checked={showClosed}
                onChange={toggleShowClosed}
              />
              <Form.Check
                type="switch"
                label={`Show Archived (${facetCounts.archived.get(true)!.toFixed(0)})`}
                checked={showArchived}
                onChange={toggleShowArchived}
              />
              <div>
                <Form.Group className="form-floating">
                  <Form.Select value={showTypes} onChange={onShowTypeChanged} className="w-auto">
                    {orderTypes.map((type) => (
                      <option key={type} value={type}>
                        {type} ({facetCounts.type.get(type)})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Label>Order Type</Form.Label>
                </Form.Group>
              </div>
            </div>
          </div>
        </div>
        <TableWrapper className="col">
          <DataTable
            bordered
            hover
            columns={columns}
            data={filteredData}
            initialState={{
              sorting: [{ id: "dateOpened", desc: true }],
            }}
            // Add relative positioning to the data rows so that we can use the 'stretched-link' class
            meta={{ dataRowProps: { className: "position-relative" } }}
          />
        </TableWrapper>
      </Container>
    </ErrorBoundary>
  );
};

function formatCustomerLocation(customer: OrderJob["customer"] | null) {
  if (!customer) return "";
  const { city, stateProvince, country } = customer.billing;
  return [city, stateProvince, country].filter((x) => x).join(", ");
}
